import * as Sentry from "@sentry/react";
import axios from "axios";

import { hasIgnoredApiErrorBreadcrumb } from "./AxiosInstance";
import {
  buildApiErrorFingerprint,
  buildIgnoredApiErrorBreadcrumb,
  shouldIgnoreApiError,
} from "./apiErrorReporting";
import { matchExpectedBusinessResponse } from "./expectedBusinessResponses";

/**
 * The second place where an API failure reaches Sentry: TanStack Query's
 * `QueryCache.onError` and `MutationCache.onError`. It is not redundant with
 * the axios interceptor — five modules call bare `axios` (credit reports, zip
 * codes, e-mail validators) and only reach Sentry through here — so it applies
 * the SAME rules instead of its own copy of them.
 *
 * Extracted out of `main.tsx` so it can be tested: the equivalence between the
 * fingerprint produced here and the one produced by the interceptor is the
 * whole point of the change, and `main.tsx` cannot be imported from a test.
 */
export interface QueryErrorContext {
  queryKey: readonly unknown[];
  queryHash: string;
  state: { status: string };
}

export interface MutationErrorContext {
  options: { mutationKey?: readonly unknown[] };
  state: { status: string };
}

/**
 * Shared body of both cache reporters: same ignore rules, same auth exemption
 * and same fingerprint as the axios interceptor. Only the tag and the extra
 * context change between a query and a mutation.
 */
function reportCacheError(
  error: unknown,
  tag: "query_error" | "mutation_error",
  contextName: "query" | "mutation",
  context: Record<string, unknown>,
): void {
  // Same rule as the axios interceptor: aborted/cancelled requests and
  // transport failures are never captured. Shared so the two lists cannot
  // drift apart again.
  if (shouldIgnoreApiError(error)) {
    // Only breadcrumb what the interceptor did not already breadcrumb.
    // Anything sent through `AxiosInstance` was seen there first, on every
    // retry; what reaches this point unmarked is bare-`axios` traffic, which
    // the interceptor never sees. See `errorsWithIgnoredBreadcrumb` in
    // `AxiosInstance.ts` for why writing both copies was harmful.
    if (!hasIgnoredApiErrorBreadcrumb(error)) {
      Sentry.addBreadcrumb(buildIgnoredApiErrorBreadcrumb(error));
    }
    return;
  }

  // Same exemption as the interceptor: 401/403 are part of the auth flow.
  // Without this the caches would keep reporting them and, now that they are
  // fingerprinted per endpoint, would open one new issue per endpoint.
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status === 401 || status === 403) {
    return;
  }

  // Respuestas de negocio ESPERADAS. Antes aqui vivia UN `if` hardcodeado para
  // el NIP incorrecto (status 400/422 + post + `code === "1009"` + url que
  // empezaba por el literal `${API}/v2/reports/`); ahora es la tabla
  // declarativa de `expectedBusinessResponses.ts`, que ademas cubre el folio
  // que ya tiene contrato, el enlace publico de multireporte caducado, el CP
  // inexistente y la investigacion borrada, y que el interceptor y
  // `beforeSend` aplican igual. El bloque viejo vivia solo aqui y se rompia si
  // la llamada era relativa.
  //
  // La migaja se escribe SOLO si el interceptor no dejo ya la suya: lo que
  // llega aqui sin marcar es trafico de `axios` pelado (el reporte de credito,
  // los codigos postales), para el que este es su UNICO contexto. El `reason`
  // es el id de la regla, no el mensaje generico de axios, y nunca el cuerpo de
  // la respuesta (llevaria datos del usuario).
  const expectedBusinessResponse = matchExpectedBusinessResponse(error);
  if (expectedBusinessResponse) {
    if (!hasIgnoredApiErrorBreadcrumb(error)) {
      Sentry.addBreadcrumb(
        buildIgnoredApiErrorBreadcrumb(error, expectedBusinessResponse.id),
      );
    }
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag(tag, "true");
    scope.setContext(contextName, context);

    // HTTP failures are grouped by endpoint, exactly like the interceptor does,
    // so the two events of the same failure land in one issue instead of
    // grouping by the axios stack trace (which merged unrelated endpoints and
    // hosts into a single issue).
    if (axios.isAxiosError(error)) {
      scope.setFingerprint(
        buildApiErrorFingerprint({
          method: error.config?.method,
          url: error.config?.url,
          status: error.response?.status,
          message: error.message,
        }),
      );
    }

    Sentry.captureException(error);
  });
}

export function reportQueryError(
  error: unknown,
  query: QueryErrorContext,
): void {
  reportCacheError(error, "query_error", "query", {
    queryKey: JSON.stringify(query.queryKey),
    queryHash: query.queryHash,
    state: query.state.status,
  });
}

/**
 * Las mutaciones fallidas no llegaban a Sentry por NINGUNA de las dos puertas.
 *
 * Las que usan `AxiosInstance` si las ve el interceptor, pero las de
 * `api/creditReport.ts` —arrancar el reporte PF/PM, confirmar el PIN,
 * reenviarlo— usan `axios` pelado, y sin `MutationCache.onError` su unica
 * salida era que un componente volviera a lanzar el error. Es decir: el paso
 * en el que el inquilino entrega sus datos al buro fallaba en silencio.
 *
 * La firma es la de `MutationCache.onError`; `variables` y `context` no se
 * usan a proposito: `variables` lleva los datos que el usuario acaba de
 * teclear (CURP, RFC, domicilio) y mandarlos a Sentry seria filtrar PII por la
 * puerta de atras. El `mutationKey` identifica la operacion sin llevar datos
 * de nadie.
 */
export function reportMutationError(
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: MutationErrorContext,
): void {
  reportCacheError(error, "mutation_error", "mutation", {
    mutationKey: JSON.stringify(mutation.options.mutationKey ?? null),
    state: mutation.state.status,
  });
}
